import {
  cloneTaskFromClipboard,
  remapDependencies,
  attachNestedSprintsFromClipboard,
  generateCustomFieldIds,
} from '../clipboardUtils';
import { showBusyOverlay } from '../../../../BusyOverlay';
import {
  attachPastedRootsToHost,
  insertPastedGraphBeforeHost,
  placePastedTasksAtOrigin,
  positionsFromPastedTasks,
} from '../../../hooks/handlers/paste/applyPastePlacement';
import { isLargeOperation } from './clipboardSize';

/**
 * Clone a validated Fokus clipboard payload, place it, and hand it to onPasteTasks.
 * `placement.skipAutoLayout` holds the drop until the next tidy.
 */
export async function pasteClipboardData({
  clipboardData,
  placement = null,
  onPasteTasks,
  setSelectedNodes,
}) {
  const targetPosition = placement?.targetPosition || null;
  const attachToHostId = placement?.attachToHostId || null;
  const attachBranch = placement?.attachBranch || null;
  const insertBeforeHost = placement?.insertBeforeHost || null;
  const skipAutoLayout = Boolean(placement?.skipAutoLayout);

  const {
    tasks: clipboardTasks,
    nestedSprints: clipboardNestedSprints,
    tagDefinitions = [],
    customFieldDefinitions = [],
  } = clipboardData;

  const timestamp = Date.now();
  const idMap = new Map();
  const optionIdMap = new Map();

  const {
    definitions: newCustomFieldDefs,
    fieldIdMap: customFieldIdMap,
    optionIdMap: customFieldOptionIdMap,
  } = generateCustomFieldIds(customFieldDefinitions, timestamp);

  let clonedTasks = clipboardTasks.map((task, idx) =>
    cloneTaskFromClipboard(task, idMap, optionIdMap, timestamp, idx, customFieldIdMap, customFieldOptionIdMap)
  );

  remapDependencies(clonedTasks, idMap, optionIdMap);

  let hostPatch = null;
  let centerOnTaskId = null;
  if (skipAutoLayout && targetPosition) {
    clonedTasks = placePastedTasksAtOrigin(clonedTasks, targetPosition);
    if (insertBeforeHost) {
      const inserted = insertPastedGraphBeforeHost(insertBeforeHost, clonedTasks);
      if (inserted) {
        clonedTasks = inserted.pastedTasks;
        hostPatch = inserted.hostPatch;
        centerOnTaskId = inserted.centerTaskId;
      }
    } else if (attachToHostId) {
      clonedTasks = attachPastedRootsToHost(clonedTasks, attachToHostId, attachBranch);
    }
  } else {
    clonedTasks.forEach((task) => {
      delete task.x;
      delete task.y;
      delete task._clipboardPos;
    });
  }

  const pastePositions = skipAutoLayout ? positionsFromPastedTasks(clonedTasks) : {};
  const clonedNestedSprints = attachNestedSprintsFromClipboard(
    clonedTasks,
    clipboardNestedSprints,
    timestamp,
  );

  console.log(`📋 Pasting ${clonedTasks.length} node(s)`);

  const hideBusy = isLargeOperation(clonedTasks)
    ? showBusyOverlay('Pasting campaigns…')
    : null;
  try {
    await onPasteTasks({
      tasks: clonedTasks,
      positions: pastePositions,
      nestedSprints: clonedNestedSprints,
      tagDefinitions,
      customFieldDefinitions: newCustomFieldDefs,
      skipAutoLayout,
      hostPatch,
      centerOnTaskId,
    });
  } finally {
    hideBusy?.();
  }

  if (setSelectedNodes) {
    setSelectedNodes(new Set(clonedTasks.map((task) => task.id)));
  }

  return true;
}
